import React, { useMemo, useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Icon,
  Text,
  Badge,
  Divider,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  ButtonGroup,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
} from '@chakra-ui/react';
import {
  FaClock,
  FaProjectDiagram,
  FaUsers,
  FaCheck,
  FaEdit,
  FaStream,
  FaListUl,
  FaTable,
} from 'react-icons/fa';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';

// Helper function to format relative time
const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInMs = now.getTime() - targetDate.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    return targetDate.toLocaleDateString();
  }
};

// Helper function to get project name
const getProjectName = (project: any, allProjects: any[] = []): string => {
  if (!project) return 'Unknown Project';
  if (typeof project === 'string') {
    const foundProject = allProjects.find(p => p._id === project);
    if (foundProject) {
      return `${foundProject.projectNumber} - ${foundProject.projectName}`;
    }
    return 'Unknown Project';
  }
  if (project.projectNumber && project.projectName) {
    return `${project.projectNumber} - ${project.projectName}`;
  }
  return 'Unknown Project';
};

// Helper function to get client name
const getClientName = (client: any): string => {
  if (!client) return 'Unknown Client';
  if (typeof client === 'string') return client;
  return client.clientName;
};

// Helper function to strip HTML tags
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const RecentActivity: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const [view, setView] = useState<'timeline' | 'table' | 'list'>('timeline');

  // Fetch data
  const { data: tasks, isLoading: tasksLoading, error: tasksError } = useTasks();
  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects();
  const { data: clients, isLoading: clientsLoading, error: clientsError } = useClients();

  // Generate activities from real data
  const activities = useMemo(() => {
    const allActivities: Array<{
      icon: any;
      color: string;
      text: string;
      badge: { text: string; colorScheme: string };
      timestamp: Date;
    }> = [];

    // Add task activities
    if (tasks) {
      // Recent task completions
      const completedTasks = tasks
        .filter(task => task.status === 'Completed')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 3);

      completedTasks.forEach(task => {
        allActivities.push({
          icon: FaCheck,
          color: 'green.500',
          text: `Task "${stripHtmlTags(task.description)}" was completed`,
          badge: { text: formatRelativeTime(task.updatedAt), colorScheme: 'green' },
          timestamp: new Date(task.updatedAt),
        });
      });

      // Recent task updates
      const recentlyUpdatedTasks = tasks
        .filter(task => task.status !== 'Completed')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 2);

      recentlyUpdatedTasks.forEach(task => {
        allActivities.push({
          icon: FaEdit,
          color: 'blue.500',
          text: `Task "${stripHtmlTags(task.description)}" was updated`,
          badge: { text: formatRelativeTime(task.updatedAt), colorScheme: 'blue' },
          timestamp: new Date(task.updatedAt),
        });
      });

      // Recently created tasks
      const recentTasks = tasks
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 2);

      recentTasks.forEach(task => {
        allActivities.push({
          icon: FaClock,
          color: 'orange.500',
          text: `New task "${stripHtmlTags(task.description)}" created${task.project ? ` for ${getProjectName(task.project, projects)}` : ''}`,
          badge: { text: formatRelativeTime(task.createdAt), colorScheme: 'orange' },
          timestamp: new Date(task.createdAt),
        });
      });
    }

    // Add project activities
    if (projects) {
      const recentProjects = projects
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 2);

      recentProjects.forEach(project => {
        allActivities.push({
          icon: FaProjectDiagram,
          color: 'purple.500',
          text: `New project "${project.projectName}" created`,
          badge: { text: formatRelativeTime(project.createdAt), colorScheme: 'purple' },
          timestamp: new Date(project.createdAt),
        });
      });
    }

    // Add client activities
    if (clients) {
      const recentClients = clients
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 2);

      recentClients.forEach(client => {
        allActivities.push({
          icon: FaUsers,
          color: 'teal.500',
          text: `Client "${client.clientName}" added to system`,
          badge: { text: formatRelativeTime(client.createdAt), colorScheme: 'teal' },
          timestamp: new Date(client.createdAt),
        });
      });
    }

    // Sort all activities by timestamp and return the most recent 8
    return allActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 8);
  }, [tasks, projects, clients]);

  // Loading state
  if (tasksLoading || projectsLoading || clientsLoading) {
    return (
      <Box>
        <Heading size="lg" mb={6} color="gray.800">
          Recent Activity
        </Heading>
        <Box
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="lg"
          border="1px"
          borderColor="gray.100"
          p={6}
        >
          <VStack align="center" spacing={4}>
            <Spinner size="lg" color="blue.500" />
            <Text color="gray.500">Loading recent activities...</Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  // Error state
  if (tasksError || projectsError || clientsError) {
    return (
      <Box>
        <Heading size="md" mb={6} color="gray.800">
          Recent Activity
        </Heading>
        <Box
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="lg"
          border="1px"
          borderColor="gray.100"
          p={6}
        >
          <Alert status="error">
            <AlertIcon />
            Failed to load recent activities. Please try again later.
          </Alert>
        </Box>
      </Box>
    );
  }

  // No activities
  if (activities.length === 0) {
    return (
      <Box>
        <Heading size="lg" mb={6} color="gray.800">
          Recent Activity
        </Heading>
        <Box
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="lg"
          border="1px"
          borderColor="gray.100"
          p={6}
        >
          <VStack align="center" spacing={4}>
            <Icon as={FaClock} boxSize={8} color="gray.400" />
            <Text color="gray.500" textAlign="center">
              No recent activities found.
              <br />
              Start by creating tasks, projects, or clients!
            </Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  // Timeline representation
  const TimelineView = () => (
    <VStack align="stretch" spacing={0} position="relative" gap={6}>
      {activities.map((activity, index) => (
        <Flex key={index} align="center" position="relative">
          <Box position="relative" zIndex={1}>
            <Icon as={activity.icon} color={activity.color} boxSize={5} />
          </Box>
          <Box flex={1} ml={3}>
            <Text fontSize="sm">{activity.text}</Text>
            <Badge colorScheme={activity.badge.colorScheme} fontSize="xs" mt={1}>
              {activity.badge.text}
            </Badge>
          </Box>
          {index < activities.length - 1 && (
            <Box
              position="absolute"
              left="10px"
              top="32px"
              bottom="-8px"
              width="2px"
              bg="gray.200"
              zIndex={0}
            />
          )}
        </Flex>
      ))}
    </VStack>
  );

  // Table representation
  const TableView = () => (
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th>Description</Th>
          <Th>When</Th>
        </Tr>
      </Thead>
      <Tbody>
        {activities.map((activity, idx) => (
          <Tr key={idx}>
            <Td>
              <Icon as={activity.icon} color={activity.color} boxSize={4} />
            </Td>
            <Td>{activity.text}</Td>
            <Td>
              <Badge colorScheme={activity.badge.colorScheme}>{activity.badge.text}</Badge>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  // List representation (original)
  const ListView = () => (
    <VStack align="stretch" spacing={4}>
      {activities.map((activity, index) => (
        <React.Fragment key={index}>
          <HStack spacing={3}>
            <Icon as={activity.icon} color={activity.color} boxSize={4} />
            <Text flex={1} fontSize="sm">
              {activity.text}
            </Text>
            <Badge colorScheme={activity.badge.colorScheme} fontSize="xs">
              {activity.badge.text}
            </Badge>
          </HStack>
          {index < activities.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </VStack>
  );

  return (
    <Box>
      <Heading size="lg" mb={6} color="gray.800">
        Recent Activity
      </Heading>
      <ButtonGroup size="sm" mb={4}>
        <Button
          leftIcon={<FaStream />}
          variant={view === 'timeline' ? 'solid' : 'ghost'}
          colorScheme={view === 'timeline' ? 'blue' : 'gray'}
          onClick={() => setView('timeline')}
        >
          Timeline
        </Button>
        <Button
          leftIcon={<FaTable />}
          variant={view === 'table' ? 'solid' : 'ghost'}
          colorScheme={view === 'table' ? 'blue' : 'gray'}
          onClick={() => setView('table')}
        >
          Table
        </Button>
        <Button
          leftIcon={<FaListUl />}
          variant={view === 'list' ? 'solid' : 'ghost'}
          colorScheme={view === 'list' ? 'blue' : 'gray'}
          onClick={() => setView('list')}
        >
          List
        </Button>
      </ButtonGroup>
      <Box bg={cardBg} borderRadius="2xl" boxShadow="lg" border="1px" borderColor="gray.100" p={6}>
        {view === 'timeline' && <TimelineView />}
        {view === 'table' && <TableView />}
        {view === 'list' && <ListView />}
      </Box>
    </Box>
  );
};

export default RecentActivity;
